import React, { useState } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table';

// UI & Styling
import { Button, Table, Form, InputGroup, Pagination } from 'react-bootstrap';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { FaSort, FaSortUp, FaSortDown, FaSearch, FaFileCsv } from 'react-icons/fa';
import papaparse from 'papaparse';
import './ReusableAdminTable.css';

// A generic skeleton loader for the table body
const SkeletonLoader = ({ rows, cols }) => (
    [...Array(rows)].map((_, i) => (
        <tr key={i}>
            {[...Array(cols)].map((_, j) => (
                <td key={j}><Skeleton height={20} /></td>
            ))}
        </tr>
    ))
);

/**
 * A feature-rich, reusable table component for the admin panel.
 * @param {Object} props
 * @param {Array} props.data - The data array to display.
 * @param {Array} props.columns - The column definitions for TanStack Table.
 * @param {boolean} props.isLoading - The loading state to show the skeleton loader.
 * @param {string} props.title - The title to display above the table.
 * @param {Function} [props.onAddNew] - Optional function to handle the "Add New" button click.
 * @param {string} [props.addNewButtonText="Add New"] - The text for the "Add New" button.
 * @param {string} [props.searchPlaceholder="Search..."] - The placeholder for the global search input.
 * @param {string} [props.exportCsvFilename="data.csv"] - The filename for the exported CSV file.
 */
const ReusableAdminTable = ({
    data,
    columns,
    isLoading,
    title,
    onAddNew,
    addNewButtonText = "Add New",
    searchPlaceholder = "Search...",
    exportCsvFilename = "data.csv"
}) => {
    // React Table State
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    // --- TanStack Table Instance ---
    const table = useReactTable({
        data,
        columns,
        state: { globalFilter, sorting, pagination },
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    // --- Event Handlers ---
    const handleExportCSV = () => {
        const dataToExport = table.getFilteredRowModel().rows.map(row => row.original);
        const csv = papaparse.unparse(dataToExport);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', exportCsvFilename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="p-2">
            {/* Page Header and Toolbar */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">

                {/* LEFT SIDE — TITLE */}
                <div>
                    <h4 className="page-title-dark mb-0">{title}</h4>
                </div>

                {/* RIGHT SIDE — SEARCH + ACTIONS */}
                <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">

                    {/* SEARCH */}
                    <InputGroup style={{ width: "260px" }}>
                        <InputGroup.Text className="bg-dark border-secondary text-white">
                            <FaSearch />
                        </InputGroup.Text>

                        <Form.Control
                            type="text"
                            placeholder={searchPlaceholder}
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            className="bg-dark border-secondary text-white"
                        />
                    </InputGroup>

                    {/* EXPORT */}
                    <Button
                        variant="outline-light"
                        onClick={handleExportCSV}
                        className="d-flex align-items-center gap-2"
                    >
                        <FaFileCsv /> Export CSV
                    </Button>

                    {/* ADD BUTTON */}
                    {onAddNew && (
                        <Button
                            onClick={onAddNew}
                            className="d-flex align-items-center gap-2"
                            style={{
                                backgroundColor: "#c30000",
                                border: "none",
                                fontWeight: "500",
                                padding: "8px 16px"
                            }}
                        >
                            + {addNewButtonText}
                        </Button>
                    )}

                </div>

            </div>

            {/* Table */}
            <SkeletonTheme baseColor="#1a1a1a" highlightColor="#2a2a2a">
                <div className="table-responsive rounded-3 admin-table-wrapper">
                    <Table hover borderless className="admin-custom-table mb-0">
                        <thead className="admin-table-thead">
                            {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map(header => {
                                        const isActionColumn = header.id?.toLowerCase().includes('action') || header.id === '#' || header.id === 'select';
                                        const canSort = !isActionColumn && header.column.getCanSort();
                                        return (
                                            <th 
                                                key={header.id} 
                                                onClick={canSort ? header.column.getToggleSortingHandler() : undefined} 
                                                style={{ cursor: canSort ? 'pointer' : 'default' }}
                                                className="admin-table-th"
                                            >
                                                <div className={`admin-th-content ${isActionColumn ? 'justify-content-center w-100' : ''}`}>
                                                    <span className="admin-th-label">
                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                    </span>
                                                    {canSort && (
                                                        <span className="admin-sort-icon-box">
                                                            {{
                                                                asc: <FaSortUp className="admin-sort-active" />,
                                                                desc: <FaSortDown className="admin-sort-active" />
                                                            }[header.column.getIsSorted()] ?? <FaSort className="admin-sort-icon" />}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <SkeletonLoader rows={pagination.pageSize} cols={columns.length} />
                            ) : table.getRowModel().rows.length > 0 ? (
                                table.getRowModel().rows.map(row => (
                                    <tr key={row.id}>
                                        {row.getVisibleCells().map(cell => (
                                            <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan={columns.length} className="text-center py-4">No data found.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </SkeletonTheme>

            {/* Pagination Controls */}
            {!isLoading && data.length > 0 && (
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3 gap-3">
                    <span className="text-secondary-dark">
                        Page{' '}<strong>{table.getState().pagination.pageIndex + 1} of {table.getPageCount()}</strong>
                    </span>
                    <Pagination className="mb-0">
                        <Pagination.First onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()} />
                        <Pagination.Prev onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} />
                        <Pagination.Next onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} />
                        <Pagination.Last onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()} />
                    </Pagination>
                </div>
            )}
        </div>
    );
};

export default ReusableAdminTable;